import { AssistantTravelAgency } from '@mews-commander/data-access';
import { IconName } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { RouteName } from '@mews-commander/enums';
import { getInlineAddress } from '@mews-commander/utils';
import { ResultListProps } from '../results/ListTitle';
import { stringifyPrimaryText } from '../../utils/array';
import { StyledLeftIcon } from '../styles';
import { highlight } from '../../utils/highlight';
import { navigateInBaseModal } from '../../../app/actions/common';
import { SearchType } from '../../assistantSearchTypes';
import { DescriptionItem, SearchItemDescription } from '../components/SearchItemDescription';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface TravelAgencyListProps extends ResultListProps {
    travelAgencies: AssistantTravelAgency[];
}

interface PrimaryTextProps {
    travelAgency: AssistantTravelAgency;
    searchTerm: string;
}

const PrimaryText = ({ travelAgency, searchTerm }: PrimaryTextProps) => {
    const props: string[] = [travelAgency.name ?? '', ...(travelAgency.department ? [travelAgency.department] : [])];
    return <>{highlight(stringifyPrimaryText(props), searchTerm)}</>;
};

interface SecondaryTextProps {
    travelAgency: AssistantTravelAgency;
    searchTerm: string;
}

const SecondaryText = ({ travelAgency, searchTerm }: SecondaryTextProps) => {
    const inlineAddress =
        travelAgency.address &&
        getInlineAddress({
            addressLine1: travelAgency.address.addressLine1,
            city: travelAgency.address.city,
            countryCode: travelAgency.address.countryCode,
            postalCode: travelAgency.address.postalCode,
        });

    const descriptions: DescriptionItem[] = [
        ...(travelAgency.iata ? [{ value: highlight(travelAgency.iata, searchTerm) }] : []),
        ...(inlineAddress ? [{ value: highlight(inlineAddress, searchTerm) }] : []),
    ];

    return <SearchItemDescription descriptions={descriptions} />;
};

export const useTravelAgencyList = ({
    travelAgencies,
    searchTerm,
}: TravelAgencyListProps): AssistantSearchListItemProps[] => {
    const resolveRoute = useResolveRoute();

    return travelAgencies.map((travelAgency) => {
        const url = resolveRoute(RouteName.TravelAgencyContractDetail, { id: travelAgency.id });
        return {
            itemId: travelAgency.id,
            primaryText: <PrimaryText travelAgency={travelAgency} searchTerm={searchTerm} />,
            secondaryText: <SecondaryText travelAgency={travelAgency} searchTerm={searchTerm} />,
            leftIcon: <StyledLeftIcon size="small" value={IconName.TravelAgency} />,
            routeAction: navigateInBaseModal(url),
            searchType: SearchType.TravelAgency,
        };
    });
};
